import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTheme } from '@/context/ThemeProvider';
import useUserStore from '@/stores/UserStore';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { fetchResponseReport } from '@/hooks/follower';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from '@/components/ui/card';
import { DateRangePicker } from '@/components/common/date-range-picker/data-range-picker';

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getDefaultRange = () => {
  // Week-to-date: from most recent Monday to today
  const end = new Date();
  const start = new Date(end);
  const day = end.getDay(); // 0 Sunday ... 6 Saturday
  const diffToMonday = (day + 6) % 7; // converts Sunday(0)->6, Monday(1)->0
  start.setDate(end.getDate() - diffToMonday);
  return { start: formatDate(start), end: formatDate(end) };
};

export default function FollowerStats() {
  const { currentTheme } = useTheme();
  const navigate = useNavigate();
  const igOpensAvailable = useUserStore((state) => state.igOpensAvailable);
  const [{ start, end }, setRange] = useState(getDefaultRange());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [stats, setStats] = useState({
    dates: [],
    sent: [],
    response_rates: [],
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchResponseReport(start, end);
      const payload = res?.data || res; // APIClient may unwrap
      setSummary(payload.summary);
      setStats(payload.stats);
    } catch (e) {
      setError(e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    if (igOpensAvailable === false) {
      navigate('/inbox');
    }
  }, [igOpensAvailable, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDateUpdate = (values) => {
    const { range } = values;
    // DateRangePicker already handles all preset calculations internally
    setRange({ start: formatDate(range.from), end: formatDate(range.to) });
  };

  const options = useMemo(
    () => ({
      chart: {
        backgroundColor: 'transparent',
        style: { color: currentTheme === 'light' ? '#0f172a' : '#e2e8f0' },
      },
      title: { text: null },
      credits: { enabled: false },
      xAxis: {
        categories: stats.dates || [],
        lineColor: 'transparent',
        tickColor: 'transparent',
        labels: {
          style: { color: currentTheme === 'light' ? '#64748b' : '#94a3b8' },
        },
      },
      yAxis: [
        {
          title: {
            text: 'Number of Messages Sent',
            style: {
              color: currentTheme === 'light' ? '#36a2eb' : '#36a2eb',
            },
          },
          allowDecimals: false,
          gridLineColor:
            currentTheme === 'light'
              ? 'rgba(148,163,184,0.25)'
              : 'rgba(51,65,85,0.6)',
          labels: {
            style: { color: currentTheme === 'light' ? '#64748b' : '#94a3b8' },
          },
        },
        {
          title: {
            text: 'Response Rate (%)',
            style: {
              color: currentTheme === 'light' ? '#ff6384' : '#ff6384',
            },
          },
          labels: {
            format: '{value}%',
            style: { color: currentTheme === 'light' ? '#64748b' : '#94a3b8' },
          },
          opposite: true,
          gridLineWidth: 0,
          max: 100,
        },
      ],
      legend: {
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          color: currentTheme === 'light' ? '#0f172a' : '#e2e8f0',
        },
        itemHoverStyle: {
          color: currentTheme === 'light' ? '#0f172a' : '#e2e8f0',
        },
      },
      // tooltip: { shared: true, valueDecimals: 2 },
      plotOptions: {
        column: { borderColor: 'transparent' },
        series: { marker: { enabled: true } },
      },
      series: [
        {
          type: 'column',
          name: 'Messages Sent',
          data: stats.sent || [],
          color: '#3b82f6',
          yAxis: 0,
        },
        {
          type: 'spline',
          name: 'Response Rate (%)',
          data: (stats.response_rates || []).map((v) => Number(v)),
          color: '#ec4899',
          yAxis: 1,
          tooltip: { valueSuffix: '%' },
        },
      ],
    }),
    [stats, currentTheme]
  );

  if (!igOpensAvailable) {
    return null;
  }

  return (
    <div className='flex h-full flex-col p-5'>
      <div className='mb-4 flex items-center justify-between'>
        <div>
          <div className='text-xl font-bold'>
            Opening Message Response Rate Report
          </div>
          <div className='text-sm text-muted-foreground'>
            Date Range: {start} to {end}
          </div>
        </div>
        <DateRangePicker
          defaultPreset='week-to-date'
          onUpdate={handleDateUpdate}
          align='end'
          locale='en-US'
          showCompare={false}
        />
      </div>

      {/* Summary cards */}
      <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
        <Card>
          <CardHeader className='pb-2'>
            <CardDescription>Total Opening Messages Sent</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-semibold'>
              {summary?.total_sent ?? (loading ? '…' : 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardDescription>Total Responses Received</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-semibold'>
              {summary?.total_responded ?? (loading ? '…' : 0)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className='pb-2'>
            <CardDescription>Overall Response Rate</CardDescription>
          </CardHeader>
          <CardContent>
            <div className='text-3xl font-semibold'>
              {summary?.overall_response_rate != null
                ? `${summary.overall_response_rate}%`
                : loading
                  ? '…'
                  : '0%'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <div className='mt-6 rounded-lg border bg-card p-4'>
        <div className='mb-2 text-lg font-semibold'>
          Opening Message Performance Over Time
        </div>
        {loading ? (
          <div className='flex h-[400px] items-center justify-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : stats.dates?.length === 0 ? (
          <div className='flex h-[200px] items-center justify-center text-sm text-muted-foreground'>
            No data available for the selected date range
          </div>
        ) : error ? (
          <div className='p-6 text-sm text-destructive'>{error}</div>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={options} />
        )}
      </div>

      {/* Daily Breakdown Table */}
      <div className='mt-6 rounded-lg border bg-card p-4'>
        <div className='mb-4 text-lg font-semibold'>Daily Breakdown</div>
        {loading ? (
          <div className='flex h-[200px] items-center justify-center'>
            <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
          </div>
        ) : error ? (
          <div className='p-6 text-sm text-destructive'>{error}</div>
        ) : (
          <div className='overflow-y-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b bg-muted/50'>
                  <th className='px-4 py-3 text-left text-sm font-semibold'>
                    Date
                  </th>
                  <th className='px-4 py-3 text-right text-sm font-semibold'>
                    Messages Sent
                  </th>
                  <th className='px-4 py-3 text-right text-sm font-semibold'>
                    Response Rate (%)
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.dates?.length > 0 ? (
                  stats.dates.map((date, index) => (
                    <tr
                      key={date}
                      className={`border-b ${
                        index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                      }`}
                    >
                      <td className='px-4 py-3 text-sm'>{date}</td>
                      <td className='px-4 py-3 text-right text-sm'>
                        {stats.sent?.[index]?.toLocaleString() || 0}
                      </td>
                      <td className='px-4 py-3 text-right text-sm'>
                        {stats.response_rates?.[index]?.toFixed(2) || '0.00'}%
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={3}
                      className='px-4 py-8 text-center text-sm text-muted-foreground'
                    >
                      No data available for the selected date range
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
